// src/api/auth.ts
import { apiFetch } from "./api-fetch";

/** Per-feature nav state: live (navigable), locked (lock icon, not navigable),
 *  hidden (absent from the nav). */
export type FeatureState = "live" | "locked" | "hidden";

export interface UserMe {
  id: number;
  username: string;
  is_admin: boolean;
  full_name?: string | null;
  user_type?: string;
  mfa_required?: boolean;
  mfa_enabled?: boolean;
  /** True while the user is still on an admin-set/default credential and must
   *  set their own password before using the app. */
  must_change_password?: boolean;
  /** Effective per-feature states ("live" | "locked" | "hidden"). Drives the Core Nav. */
  feature_locks?: Record<string, FeatureState>;
}

export interface AuditLogEntry {
  id: number;
  username?: string | null;
  event_type: string;
  ip_address?: string | null;
  user_agent?: string | null;
  metadata?: Record<string, unknown>;
  created_at?: string | null;
}

export interface UserFeatureLocks {
  user_id: number;
  username: string;
  effective: Record<string, FeatureState>;
  overrides: Record<string, FeatureState>;
  defaults: Record<string, FeatureState>;
}

export type MfaState = "none" | "setup_required" | "challenge";

export interface LoginResponse {
  mfa_state: MfaState;

  // Populated only when mfa_state === "none"
  access_token?: string | null;
  token_type?: string;

  // Populated only when mfa_state === "setup_required"
  setup_token?: string | null;

  // Populated only when mfa_state === "challenge"
  challenge_token?: string | null;

  username: string;
  full_name?: string | null;
  is_admin: boolean;
  user_type?: string;
}

export interface RegisterResponse {
  id: number;
  username: string;
  is_admin: boolean;
  full_name?: string | null;
  user_type?: string;
  mfa_required?: boolean;
}

export interface AdminCreateUserRequest {
  username: string;
  /** Temporary. The user is forced to replace it on first sign-in. */
  password: string;
  full_name?: string | null;
  user_type?: string;
  is_admin?: boolean;
  mfa_required?: boolean;
}

/** What POST /auth/admin/create-user returns — note `id`, which the caller
 *  needs to assign brands in the same flow. */
export interface AdminCreatedUser {
  id: number;
  username: string;
  is_admin: boolean;
  full_name?: string | null;
  user_type: string;
  mfa_required: boolean;
  created_by: string;
}

export interface AdminUpdateUserRequest {
  username?: string | null;
  password?: string | null;
  is_admin?: boolean | null;
  full_name?: string | null;
  user_type?: string | null;
  mfa_required?: boolean | null;
  reset_mfa?: boolean | null;
}

// ─── MFA payloads ───────────────────────────────────────────
export interface MfaSetupInitResponse {
  otpauth_uri: string;
  secret_b32: string;
}

export interface MfaSetupVerifyResponse {
  access_token: string;
  token_type: string;
  backup_codes: string[];
  username: string;
  full_name?: string | null;
  is_admin: boolean;
  user_type?: string;
}

export interface MfaVerifyResponse {
  access_token: string;
  token_type: string;
  username: string;
  full_name?: string | null;
  is_admin: boolean;
  user_type?: string;
  backup_code_used: boolean;
  backup_codes_remaining: number;
}

export interface MfaStatusResponse {
  mfa_enabled: boolean;
  mfa_required: boolean;
  backup_codes_remaining: number;
  enrolled_at?: string | null;
  last_used_at?: string | null;
}

// ─── Public config ──────────────────────────────────────────
/** Which sign-in paths this deployment exposes. Read before auth. */
export interface AuthPublicConfig {
  registration_enabled: boolean;
}

/** The global signup switch, as Global Settings reads and writes it. */
export interface RegistrationSettings {
  enabled: boolean;
}

// ─── SSO / OIDC ─────────────────────────────────────────────
export interface SsoPublicConfig {
  enabled: boolean;
  authorization_endpoint?: string | null;
  token_endpoint?: string | null;
  client_id?: string | null;
  redirect_uri?: string | null;
  scopes?: string | null;
  /** At least one customer connection is on: show the SSO button and ask
   *  for a work email to find the right IdP. */
  discovery?: boolean;
  /** Set when /auth/sso/discover routed to a customer connection. */
  connection_id?: string | null;
  connection_name?: string | null;
}

/** A customer's own OIDC IdP (e.g. their Okta org), bound to their domains.
 *  The client secret is never returned — only whether one is set. */
export interface SsoConnection {
  id: string;
  name: string;
  issuer: string;
  client_id: string;
  client_secret_set: boolean;
  /** Blank = the global SSO redirect URI (or the site origin). */
  redirect_uri: string;
  domains: string[];
  /** Brands an account created by this connection starts with. Granted once,
   *  at creation, as ordinary brand assignments — so they show in User
   *  Management and Associate Brands. Empty = no brand access until an admin
   *  assigns it. */
  default_brands: string[];
  /** A first sign-in may create an Upmode account. */
  jit_enabled: boolean;
  enabled: boolean;
  /** Only on an update that changed default_brands: what that did to the
   *  accounts already at these domains. */
  brands_synced?: SsoConnectionBackfill | null;
}

/** Outcome of applying a connection's default brands to the accounts that
 *  already exist at its domains. */
export interface SsoConnectionBackfill {
  connection_id: string;
  brands: string[];
  users_matched: number;
  users_updated: number;
  brands_added: number;
  brands_removed: number;
}

export type SsoConnectionInput = Omit<
  SsoConnection, "id" | "client_secret_set" | "brands_synced"
> & {
  /** Write-only. Required on create; omit on update to keep the stored one. */
  client_secret?: string;
};

/** What the SPA posts back when the IdP redirects to us. */
export type SsoCallbackPayload =
  /** Global SSO: the browser already redeemed the code. */
  | { id_token: string; nonce?: string | null }
  /** Customer connection: the backend redeems the code with the secret. */
  | { connection_id: string; code: string; code_verifier: string;
      redirect_uri: string; nonce: string };

export interface SsoAdminConfig {
  enabled: boolean;
  issuer?: string | null;
  client_id?: string | null;
  client_secret_set: boolean;
  redirect_uri?: string | null;
  scopes?: string | null;
  admin_group?: string | null;
}

export interface SsoConfigUpdate {
  enabled?: boolean;
  issuer?: string;
  client_id?: string;
  client_secret?: string;
  redirect_uri?: string;
  scopes?: string;
  admin_group?: string;
}


// Pre-auth endpoints: a 401 here means "wrong credentials / bad code",
// not "session expired" — pass allowAnonymous so the global expiry
// interceptor in api-fetch doesn't bounce the user off the login page
// during a failed login attempt.
const PRE_AUTH = { allowAnonymous: true } as const;

export const authApi = {
  login: (credentials: { username: string; password: string; rememberMe?: boolean }) =>
    apiFetch<LoginResponse>("/auth/login", {
      method: "POST",
      ...PRE_AUTH,
      body: JSON.stringify({
        username:    credentials.username,
        password:    credentials.password,
        remember_me: credentials.rememberMe ?? true,
      }),
    }),

  register: (payload: { username: string; password: string; full_name?: string | null }) =>
    apiFetch<RegisterResponse>("/auth/register", {
      method: "POST",
      ...PRE_AUTH,
      body: JSON.stringify(payload),
    }),

  getMe: () => apiFetch<UserMe>("/auth/me"),

  /** Forced first-login password set. Only valid while must_change_password
   *  is true; clears the flag and keeps the session alive. */
  completeInitialPassword: (new_password: string) =>
    apiFetch<{ ok: boolean }>("/auth/complete-initial-password", {
      method: "POST",
      body: JSON.stringify({ new_password }),
    }),

  // ─── MFA ───
  mfaSetupInit: (setup_token: string) =>
    apiFetch<MfaSetupInitResponse>("/auth/mfa/setup-init", {
      method: "POST",
      ...PRE_AUTH,
      body: JSON.stringify({ setup_token }),
    }),

  mfaSetupVerify: (setup_token: string, code: string) =>
    apiFetch<MfaSetupVerifyResponse>("/auth/mfa/setup-verify", {
      method: "POST",
      ...PRE_AUTH,
      body: JSON.stringify({ setup_token, code }),
    }),

  mfaVerify: (challenge_token: string, code: string, is_backup_code = false) =>
    apiFetch<MfaVerifyResponse>("/auth/mfa/verify", {
      method: "POST",
      ...PRE_AUTH,
      body: JSON.stringify({ challenge_token, code, is_backup_code }),
    }),

  mfaStatus: () => apiFetch<MfaStatusResponse>("/auth/mfa/status"),

  /** Public. Whether self-service signup is open on this deployment. The
   *  server refuses /auth/register when it isn't, so this only decides
   *  whether the SPA offers the link. */
  config: () =>
    apiFetch<AuthPublicConfig>("/auth/config", PRE_AUTH),

  /** Read the global signup switch. Admin only. The login page uses the
   *  public config() above instead — this one carries the admin view. */
  getRegistrationSetting: () =>
    apiFetch<RegistrationSettings>("/auth/admin/registration"),

  /** Open or close self-service signup platform-wide. Admin only (the
   *  backend enforces 403 otherwise) and audited server-side. */
  setRegistrationSetting: (enabled: boolean) =>
    apiFetch<RegistrationSettings>("/auth/admin/registration", {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),

  // ─── SSO / OIDC ───
  ssoConfig: () =>
    apiFetch<SsoPublicConfig>("/auth/sso/config", PRE_AUTH),

  /** Which IdP a work email signs in with: its company's connection, else
   *  the global SSO config. 404 when neither applies. */
  ssoDiscover: (email: string) =>
    apiFetch<SsoPublicConfig>("/auth/sso/discover", {
      method: "POST",
      ...PRE_AUTH,
      body: JSON.stringify({ email }),
    }),

  ssoCallback: (payload: SsoCallbackPayload) =>
    apiFetch<LoginResponse>("/auth/sso/callback", {
      method: "POST",
      ...PRE_AUTH,
      body: JSON.stringify(payload),
    }),

  getSsoAdminConfig: () => apiFetch<SsoAdminConfig>("/auth/admin/sso"),

  updateSsoAdminConfig: (payload: SsoConfigUpdate) =>
    apiFetch<SsoAdminConfig>("/auth/admin/sso", {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  listSsoConnections: () =>
    apiFetch<SsoConnection[]>("/auth/admin/sso/connections"),

  createSsoConnection: (payload: SsoConnectionInput) =>
    apiFetch<SsoConnection>("/auth/admin/sso/connections", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateSsoConnection: (id: string, payload: Partial<SsoConnectionInput>) =>
    apiFetch<SsoConnection>(`/auth/admin/sso/connections/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  /** Bring accounts already at the connection's domains in line with its
   *  default brands. Withdraws only what this connection granted. */
  applySsoConnectionBrands: (id: string) =>
    apiFetch<SsoConnectionBackfill>(
      `/auth/admin/sso/connections/${encodeURIComponent(id)}/apply-brands`,
      { method: "POST" },
    ),

  deleteSsoConnection: (id: string) =>
    apiFetch<void>(`/auth/admin/sso/connections/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  mfaRegenerateBackupCodes: () =>
    apiFetch<{ backup_codes: string[] }>("/auth/mfa/regenerate-backup-codes", {
      method: "POST",
    }),

  // ─── Admin ───
  listUsers: () => apiFetch<UserMe[]>("/auth/admin/users"),

  auditLog: (params: {
    username?: string;
    event_type?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.username)   qs.set("username", params.username);
    if (params.event_type) qs.set("event_type", params.event_type);
    qs.set("limit",  String(params.limit  ?? 100));
    qs.set("offset", String(params.offset ?? 0));
    return apiFetch<AuditLogEntry[]>(`/auth/admin/audit-log?${qs.toString()}`);
  },

  /** Provision a user. Admin only. The password set here is temporary: the
   *  backend flags must_change_password, so the user picks their own on first
   *  sign-in and this one stops working. */
  createUser: (payload: AdminCreateUserRequest) =>
    apiFetch<AdminCreatedUser>("/auth/admin/create-user", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateUser: (userId: number, payload: AdminUpdateUserRequest) =>
    apiFetch<UserMe>(`/auth/admin/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  deleteUser: (userId: number) =>
    apiFetch<void>(`/auth/admin/users/${userId}`, { method: "DELETE" }),

  // ─── Feature locks (entitlements) ───
  getUserFeatureLocks: (userId: number) =>
    apiFetch<UserFeatureLocks>(`/auth/admin/users/${userId}/feature-locks`),

  setUserFeatureLocks: (userId: number, feature_locks: Record<string, FeatureState>) =>
    apiFetch<UserFeatureLocks>(`/auth/admin/users/${userId}/feature-locks`, {
      method: "PUT",
      body: JSON.stringify({ feature_locks }),
    }),
};